import { Box, BoxProps, Icon, IconProp, MenuItem } from '@ff-it/ui';
import { ActionDropdown, ButtonGroupField } from 'components';
import { PlanRow } from 'modules/campaign/row';
import { ReactElement } from 'react';
import { usePlanMeta, usePlanMutations } from '../state';
import { toast } from 'react-toastify';
import { transparencyIconOptions } from 'options';
import { TransparencyIcon } from 'components/Icon';
import { required } from 'utilities';
import { openBulkUpdateDialog } from './openBulkUpdateDialog';

type BulkActionConfig = {
  action: string;
  icon: IconProp;
  label: string;
  color?: BoxProps['color'];
  filterCandidate: (r: PlanRow) => boolean;
  handler: (rows: PlanRow[]) => Promise<void>;
};

function useBulkActions(selected: PlanRow[], celarSelection: () => void): (ReactElement | '-')[] {
  const { planning } = usePlanMeta();
  const { copyRows, update } = usePlanMutations();

  // @TODO, validate new value, at least one row
  // if we ever allow picking rows or render rows that won't be updated, render new value
  const actionConfig: (BulkActionConfig | '-')[] = [
    {
      action: 'update_transparency',
      icon: 'paper-plane',
      label: 'Update transparency',
      filterCandidate: (r: PlanRow) => r.config.transparency && !r.config.transparency.locked,
      handler: async (rows) => {
        openBulkUpdateDialog<{ transparency: string }>(rows, update, {
          dialogHeader: 'Update transparency',
          renderFields: () => (
            <ButtonGroupField
              name="transparency"
              label="New transparency value"
              variant="primary"
              options={transparencyIconOptions}
              required
              validate={required}
            />
          ),
          renderCurrentValue: (row) => (
            <>
              <TransparencyIcon transparency={row.transparency} className="mr-1" /> {row.transparency}
            </>
          ),
          buildPatch: (_row, { transparency }) => ({
            transparency,
          }),
        });
      },
    },
    {
      action: 'copy',
      icon: 'copy',
      label: 'Copy rows',
      filterCandidate: (_r: PlanRow) => planning,
      handler: async (rows): Promise<void> => {
        const created = await copyRows(rows);
        celarSelection();
        toast.success(`Copied ${created.length} rows!`);
      },
    },
    '-',
    {
      action: 'delete',
      icon: 'trash-can-xmark',
      label: 'Delete rows',
      color: 'error500',
      filterCandidate: (r: PlanRow) => planning && !r.state.is_locked,
      handler: async (rows) => {
        const res = await update({
          rows: {
            remove: rows.map((r) => r.id),
          },
        });
        const removed = res.unwrap().rows?.remove;

        celarSelection();
        removed && toast.warn(`Deleted ${removed.length} rows!`);
      },
    },
  ];

  const numSelected = selected.length;
  const actions: (ReactElement | '-')[] = actionConfig.map((a) => {
    if (a === '-') {
      return '-';
    }
    const { action, label, color, icon, filterCandidate, handler } = a;
    const candidates = selected.filter(filterCandidate);
    const numCandidates = candidates.length;
    const disabled = numCandidates === 0;

    return (
      <MenuItem
        testId={`action-${action}`}
        key={action}
        label={label}
        onClick={() => handler(candidates)}
        prefix={<Icon icon={icon} color={color} />}
        suffix={<Suffx numCandidates={numCandidates} numSelected={numSelected} />}
        disabled={disabled}
      />
    );
  });

  return actions;
}

type BulkActionsProps = {
  selected: PlanRow[];
  celarSelection: () => void;
};

export function BulkActions({ selected, celarSelection }: BulkActionsProps): ReactElement | null {
  const actions = useBulkActions(selected, celarSelection);

  return (
    <ActionDropdown variant="secondary" actions={actions} icon="ellipsis" size="sm" menuSize="md" testId="bulk-actions">
      Bulk actions
    </ActionDropdown>
  );
}

const badgeProps = {
  ml: '2',
  display: 'flex',
  alignItems: 'center',
  textSize: 'xxs',
  px: '1',
  borderRadius: 'xs',
  gap: '0.5',
} as const;

function Suffx({ numSelected, numCandidates }: { numSelected: number; numCandidates: number }): ReactElement {
  if (numCandidates === 0) {
    return (
      <Box {...badgeProps} background="error100" color="error700">
        <Icon icon="lock" />
        {numSelected}
      </Box>
    );
  }

  if (numCandidates === numSelected) {
    return (
      <Box {...badgeProps} background="success100" color="success700">
        <Icon icon="circle-check" />
        {numCandidates}
      </Box>
    );
  }

  return (
    <Box {...badgeProps} background="warning100" color="warning700">
      <Icon icon="lock" />
      {numCandidates} / {numSelected}
    </Box>
  );
}
